import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface ScoringConfigItem {
  id: string;
  config_key: string;
  value: number;
  label: string;
  description: string | null;
  category: string;
  updated_by: string | null;
  updated_at: string;
}

export interface ScoringConfig {
  // Membership points
  points_bronce: number;
  points_plata: number;
  points_oro: number;
  // Redemption points
  points_canje: number;
  points_pasivo: number;
  // Savings points
  points_ahorro_base: number;
  ahorro_threshold: number;
  // Volume bonuses (economic)
  bonus_bronce_10: number;
  bonus_plata_10: number;
  bonus_oro_10: number;
  // Monthly prizes
  premio_1er: number;
  premio_2do: number;
  premio_3er: number;
}

const DEFAULT_CONFIG: ScoringConfig = {
  points_bronce: 10,
  points_plata: 20,
  points_oro: 40,
  points_canje: 8,
  points_pasivo: 2,
  points_ahorro_base: 10,
  ahorro_threshold: 10000,
  bonus_bronce_10: 12000,
  bonus_plata_10: 20000,
  bonus_oro_10: 40000,
  premio_1er: 40000,
  premio_2do: 30000,
  premio_3er: 20000,
};

export const useScoringConfig = () => {
  const [configItems, setConfigItems] = useState<ScoringConfigItem[]>([]);
  const [config, setConfig] = useState<ScoringConfig>(DEFAULT_CONFIG);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchConfig = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('scoring_config')
        .select('*')
        .order('category', { ascending: true });

      if (fetchError) throw fetchError;

      if (data) {
        setConfigItems(data);
        
        // Build config object from items
        const newConfig = { ...DEFAULT_CONFIG };
        data.forEach((item) => {
          if (item.config_key in newConfig) {
            (newConfig as any)[item.config_key] = item.value;
          }
        });
        setConfig(newConfig);
      }
    } catch (err) {
      console.error('Error fetching scoring config:', err);
      setError(err instanceof Error ? err.message : 'Error loading config');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const updateConfigValue = async (configKey: string, newValue: number): Promise<boolean> => {
    try {
      const { error: updateError } = await supabase
        .from('scoring_config')
        .update({ 
          value: newValue,
          updated_at: new Date().toISOString()
        })
        .eq('config_key', configKey);

      if (updateError) throw updateError;

      // Update local state
      setConfig(prev => ({
        ...prev,
        [configKey]: newValue
      }));

      setConfigItems(prev => 
        prev.map(item => 
          item.config_key === configKey 
            ? { ...item, value: newValue, updated_at: new Date().toISOString() }
            : item
        )
      );

      toast.success('Valor actualizado correctamente');
      return true;
    } catch (err) {
      console.error('Error updating config:', err);
      toast.error('Error al actualizar el valor');
      return false;
    }
  };

  const getConfigValue = (key: keyof ScoringConfig): number => {
    return config[key] ?? DEFAULT_CONFIG[key];
  };

  const getConfigItemsByCategory = (category: string): ScoringConfigItem[] => {
    return configItems.filter(item => item.category === category);
  };

  return {
    config,
    configItems,
    loading,
    error,
    updateConfigValue,
    getConfigValue,
    getConfigItemsByCategory,
    refetch: fetchConfig,
  };
};
